import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import type { ScheduleType } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminScheduleTypes, deleteScheduleType } from '../lib/api';
import { Spinner } from '@tsuyosa/ui';

export default function ScheduleTypesPage() {
  const { accessToken } = useAuth();
  const [types, setTypes] = useState<ScheduleType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchTypes() {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const data = await getAdminScheduleTypes(accessToken);
      setTypes(data.sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule types');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchTypes();
  }, [accessToken]);

  async function handleDelete(id: string, name: string) {
    if (!accessToken) return;
    if (!confirm(`Hapus type "${name}"? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await deleteScheduleType(accessToken, id);
      setTypes((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Schedule Types</h1>
          <p className="mt-0.5 text-sm text-gray-500">{types.length} tipe jadwal</p>
        </div>
        <Link
          to="/schedule-types/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--tsuyosa-pink)' }}
        >
          <Plus size={16} />
          Add Type
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : types.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">Belum ada tipe jadwal.</p>
          <Link to="/schedule-types/new" className="mt-2 inline-block text-sm text-pink-400 hover:underline">
            Tambah tipe pertama
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
                <th className="w-8 px-3 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500" />
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">ORDER</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">NAMA (ID)</th>
                <th className="hidden px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500 md:table-cell">NAMA (JP)</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">STATUS</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium tracking-wider text-gray-500">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {types.map((t) => (
                <tr key={t.id} className="bg-[var(--color-background)] hover:bg-white/[0.02] transition-colors">
                  <td className="px-3 py-3">
                    <GripVertical size={14} className="text-gray-600" />
                  </td>
                  <td className="px-4 py-3 text-gray-500 tabular-nums">
                    {t.sort_order}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-200">
                    {t.name_id}
                  </td>
                  <td className="hidden px-4 py-3 text-gray-400 md:table-cell">
                    {t.name_jp}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        t.is_active
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-gray-500/15 text-gray-500'
                      }`}
                    >
                      {t.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/schedule-types/${t.id}/edit`}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-pink-500/30 hover:text-pink-400 flex items-center gap-1.5"
                      >
                        <Pencil size={12} />
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(t.id, t.name_id)}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-red-500/30 hover:text-red-400 flex items-center gap-1.5"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
